import React from 'react';
import { motion } from 'framer-motion';
import { FaTwitter, FaGithub, FaDiscord, FaLinkedin, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  const footerLinks = [
    {
      title: "Company",
      links: [
        { name: "Executive Leadership", to: "/about" },
        { name: "Global Partnerships", to: "/partnerships" },
        { name: "Investor Relations", to: "/investors" },
        { name: "Sustainability Report", to: "/sustainability" },
      ],
    },
    {
      title: "Solutions",
      links: [
        { name: "Enterprise Platforms", to: "/solutions" },
        { name: "Industry Solutions", to: "/industries" },
        { name: "Technology Stack", to: "/technology" },
        { name: "Consulting Services", to: "/consulting" },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Compliance Center", to: "/compliance" },
        { name: "Privacy Framework", to: "/privacy" },
        { name: "Terms of Engagement", to: "/terms" },
        { name: "Security Whitepapers", to: "/security" },
        { name: "Account TakeDowm", to: "/request-account-deletion" },
        { name: "Sitemap", to: "/sitemap" },
      ],
    },
    {
      title: "Global Network",
      links: [
        { name: "Nine Star United, SRL", to: "https://ninestarunited.com" },
        { name: "Ecomusic Ventures", to: "https://ecomusic.ninestarunited.com" },
        { name: "Silver River Capital", to: "https://silverriverenterprises.ninestarunited.com" },
        { name: "PixelCraft Studios", to: "https://pixelCraft.ninestarunited.com" },
        { name: "SongGuard Technologies", to: "https://songguard.ninestarunited.com" },
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 15, opacity: 0 },
    visible: { 
      y: 0,
      opacity: 1,
      transition: { type: 'tween', duration: 0.4 }
    }
  };

  return (
    <motion.footer 
      className="bg-[#0A192F] py-20 border-t border-[#172A45]"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
        >
          {/* Branding Section */}
          <motion.div 
            className="md:col-span-2 lg:col-span-1"
            variants={itemVariants}
          >
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-4">NSU Group</h3>
              <p className="text-[#8892B0] text-sm leading-relaxed">
                Pioneering digital transformation through strategic innovation and global collaboration.
              </p>
            </div>
            
            <motion.div 
              className="flex items-center gap-4 mt-6"
              variants={containerVariants}
            >
              {[
                { icon: <FaLinkedin className="w-5 h-5" />, href: "https://linkedin.com" },
                { icon: <FaTwitter className="w-5 h-5" />, href: "https://twitter.com" },
                { icon: <FaGithub className="w-5 h-5" />, href: "https://github.com" }
              ].map((item, i) => (
                <motion.a
                  key={i}
                  href={item.href}
                  className="p-2 rounded-lg bg-[#172A45] hover:bg-[#1F3A5F] transition-colors"
                  whileHover={{ y: -2 }}
                  variants={itemVariants}
                >
                  {item.icon}
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          {/* Links Sections */}
          {footerLinks.map((section, index) => (
            <motion.div 
              key={index}
              variants={itemVariants}
            >
              <h3 className="font-medium text-[#CCD6F6] mb-5 text-sm uppercase tracking-wider">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, i) => (
                  <motion.li 
                    key={i}
                    whileHover={{ x: 3 }}
                  >
                    <Link 
                      to={link.to}
                      className="text-[#8892B0] hover:text-[#64FFDA] transition-colors flex items-center gap-2 text-sm"
                      target={link.to.startsWith('http') ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                    >
                      <FaChevronRight className="w-3 h-3 text-[#64FFDA]/50" />
                      {link.name}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Footer Bottom */}
        <motion.div 
          className="mt-20 pt-8 border-t border-[#172A45] text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-[#8892B0] text-sm">
            <div className="order-2 md:order-1">
              © {new Date().getFullYear()} Nine Star United, SRL. All rights reserved.
            </div>
            <div className="order-1 md:order-2 flex items-center gap-4">
              <Link to="/terms" className="hover:text-[#64FFDA] transition-colors">
                Terms of Engagement
              </Link>
              <span className="text-[#64FFDA]">•</span>
              <Link to="/privacy" className="hover:text-[#64FFDA] transition-colors">
                Privacy Framework
              </Link>
              <span className="text-[#64FFDA]">•</span>
              <Link to="/compliance" className="hover:text-[#64FFDA] transition-colors">
                Compliance Center
              </Link>
            </div>
          </div>
          <p className="mt-4 text-xs text-[#8892B0]/70">
            ISO 27001 Certified • SOC 2 Type II Compliant • GDPR Ready
          </p>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;